import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Content,
  Button,
  Tag,
  InlineLoading,
  InlineNotification,
} from '@carbon/react';
import {
  Add,
  Launch,
  TrashCan,
  Renew,
  CheckmarkFilled,
  ErrorFilled,
  Time,
  ListBulleted,
} from '@carbon/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { datasetApi } from '@/lib/api/dataset.api';
import { CreateDatasetModal } from '@/components/dataset/CreateDatasetModal';
import { useKnowledgeBases } from '@/lib/query/hooks';
import type { Dataset } from '@snxt/snxt-onto-share';
import './KnowledgeExplorerListPage.scss';

// ── Status helpers ──────────────────────────────────────────────────────────

function buildStatusTagType(s: string): 'green' | 'blue' | 'red' | 'gray' {
  switch (s) {
    case 'completed': return 'green';
    case 'running':   return 'blue';
    case 'failed':    return 'red';
    default:          return 'gray';
  }
}

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'completed': return <CheckmarkFilled size={14} style={{ color: 'var(--cds-support-success, #24a148)' }} />;
    case 'failed':    return <ErrorFilled     size={14} style={{ color: 'var(--cds-support-error, #da1e28)' }} />;
    case 'running':   return <Renew           size={14} style={{ color: 'var(--cds-support-info, #0043ce)', animation: 'spin 1s linear infinite' }} />;
    default:          return <Time            size={14} style={{ color: 'var(--cds-text-secondary)' }} />;
  }
}

// ── Dataset card ──────────────────────────────────────────────────────────────

function DatasetCard({ ds, onOpen, onDelete, onBuild }: {
  ds: Dataset;
  onOpen:   () => void;
  onDelete: () => void;
  onBuild:  () => void;
}) {
  const isRunning = ds.build_status === 'running';
  return (
    <div className={`ke-card${isRunning ? ' ke-card--running' : ''}`}>
      <div className="ke-card__status-strip">
        <StatusIcon status={ds.build_status} />
        <Tag size="sm" type={buildStatusTagType(ds.build_status)}>{ds.build_status}</Tag>
        {isRunning && <InlineLoading className="ke-card__running-spinner" />}
      </div>
      <div className="ke-card__header">
        <span className="ke-card__name">{ds.name}</span>
      </div>
      <div className="ke-card__meta">
        <Tag size="sm" type="outline">KB: {ds.knowledge_base_name ?? ds.knowledge_base_id}</Tag>
        {ds.rows_total != null && (
          <Tag size="sm" type="teal">{ds.rows_total} row{ds.rows_total !== 1 ? 's' : ''}</Tag>
        )}
        {ds.confidence_overall != null && (
          <Tag size="sm" type="outline">
            conf. {Math.round(ds.confidence_overall * 100)}%
          </Tag>
        )}
      </div>
      {ds.description && (
        <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', margin: '0.5rem 0 0', lineHeight: 1.4 }}>
          {ds.description}
        </p>
      )}
      <div className="ke-card__actions">
        <Button kind="ghost" size="sm" renderIcon={Launch} onClick={onOpen}>Open</Button>
        <Button
          kind="secondary"
          size="sm"
          renderIcon={Renew}
          onClick={onBuild}
          disabled={isRunning}
          iconDescription="Run / rebuild"
          hasIconOnly
        />
        <Button
          kind="ghost"
          size="sm"
          renderIcon={TrashCan}
          hasIconOnly
          iconDescription="Delete"
          onClick={onDelete}
          style={{ color: 'var(--cds-support-error, #da1e28)' }}
        />
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function DatasetsListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: kbs } = useKnowledgeBases();
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  const { data: datasets, isLoading, isError } = useQuery<Dataset[]>({
    queryKey: ['datasets'],
    queryFn: () => datasetApi.list(),
    refetchInterval: (query) => {
      const list = query.state.data as Dataset[] | undefined;
      if (!list) return false;
      return list.some((d) => d.build_status === 'running') ? 4000 : false;
    },
  });

  const createMutation = useMutation({
    mutationFn: (dto: Parameters<typeof datasetApi.create>[0]) => datasetApi.create(dto),
    onSuccess: (ds) => {
      qc.invalidateQueries({ queryKey: ['datasets'] });
      setCreateLoading(false);
      setCreateOpen(false);
      navigate(`/datasets/${ds.id}`);
    },
    onError: () => setCreateLoading(false),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => datasetApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['datasets'] }),
  });

  const buildMutation = useMutation({
    mutationFn: (id: string) => datasetApi.triggerBuild(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['datasets'] }),
  });

  return (
    <Content className="app-content">
      <div className="ke-list-page">
        <div className="ke-list-page__header">
          <div>
            <h1 className="ke-list-page__title">Datasets</h1>
            <p className="ke-list-page__subtitle">
              Extract structured, reviewable tabular data from a knowledge base, guided by an ontology.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button renderIcon={Add} onClick={() => setCreateOpen(true)}>
              New dataset
            </Button>
          </div>
        </div>

        {isLoading && <InlineLoading description="Loading datasets…" />}
        {isError && <InlineNotification kind="error" title="Failed to load datasets" hideCloseButton />}

        {!isLoading && !isError && (datasets ?? []).length === 0 && (
          <div className="ke-list-page__empty">
            <ListBulleted size={48} style={{ opacity: 0.3 }} />
            <p>No datasets yet. Create one to start extracting structured training data from your documents.</p>
            <Button kind="ghost" renderIcon={Add} onClick={() => setCreateOpen(true)}>
              Create your first dataset
            </Button>
          </div>
        )}

        {!isLoading && !isError && (datasets ?? []).length > 0 && (
          <div className="ke-list-page__grid">
            {(datasets ?? []).map((ds) => (
              <DatasetCard
                key={ds.id}
                ds={ds}
                onOpen={() => navigate(`/datasets/${ds.id}`)}
                onBuild={() => buildMutation.mutate(ds.id)}
                onDelete={() => {
                  if (window.confirm(`Delete dataset "${ds.name}"?`)) {
                    deleteMutation.mutate(ds.id);
                  }
                }}
              />
            ))}
          </div>
        )}

        <CreateDatasetModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          knowledgeBases={kbs ?? []}
          loading={createLoading}
          onCreated={(dto) => {
            setCreateLoading(true);
            createMutation.mutate(dto);
          }}
        />
      </div>
    </Content>
  );
}

// Made with Bob
